import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  Inject,
  inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSidenav } from '@angular/material/sidenav';
import { BANNER_MODEL } from '@banyan/commons/consts/builder-io-model.const';
import { FALLBACK_LOCAL, supportedLocales } from '@banyan/commons/consts/locale.const';
import { LOCALE } from '@banyan/commons/enum/locale';
import { AccountService } from '@banyan/modules/account/services/account.service';
import { customComponents } from '@banyan/modules/builder-io/builder-registry';
import { BuilderService } from '@banyan/modules/builder-io/services/builder.service';
import { CaptureNeedService } from '@banyan/modules/capture-needs/services/capture-need.service';
import { CartService } from '@banyan/modules/cart/services/cart.service';
import { CartV2Service } from '@banyan/modules/cart/services/cart-v2.service';
import { features } from '@banyan/modules/shared/constants/feature-toggling.const';
import { FeatureTogglingService } from '@banyan/modules/shared/services/feature-toggling/feature-toggling.service';
import { NotificationService } from '@banyan/modules/shared/services/notification/notification.service';
import { BuilderContent } from '@builder.io/sdk-angular';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, map, tap } from 'rxjs';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {
  @ViewChild('triggerPorfile') menuTrigger: MatMenuTrigger | undefined;
  @ViewChild('triggerCart') menuCartTrigger: MatMenuTrigger | undefined;
  @ViewChild('triggerCartV2') menuCartV2Trigger: MatMenuTrigger | undefined;
  @ViewChild('sidenav') sidenav: MatSidenav | undefined;
  @Output() sideNavToggle = new EventEmitter();
  @Input() isScrolled: boolean = false;
  preferredLanguage = new FormControl<LOCALE | null>(null);
  selectedLocal = FALLBACK_LOCAL;
  locales = supportedLocales;
  isMenuOpen = false;
  isMenuCartOpen = false;
  isMenuCartV2Open = false;
  whatsappGivaudan: string = environment.whatsappGivaudan;
  translateFileLoaded = false;
  captureNeedService = inject(CaptureNeedService);
  accountService = inject(AccountService);
  translateService = inject(TranslateService);
  notificationService = inject(NotificationService);
  cartService = inject(CartService);
  cartV2Service = inject(CartV2Service);
  builderService = inject(BuilderService);
  featureTogglingService = inject(FeatureTogglingService);
  content: BuilderContent | null = null;
  apiKey = environment.builderIoApiKey;
  query: any;

  constructor(@Inject(PLATFORM_ID) private readonly platformId: any) {
    effect(() => {
      if (this.accountService.user().isAuthenticated) {
        this.translateService.use(this.accountService.user().preferredLanguage);
        this.preferredLanguage.patchValue(this.accountService.user().preferredLanguage, { emitEvent: false });
        this.getBannerContent();
      }
    });
    // Set default locale
    if (this.featureTogglingService.isFeatureAllowed('EN', features)) {
      this.translateService.use(LOCALE.EN);
    } else {
      this.preferredLanguage.patchValue(FALLBACK_LOCAL, { emitEvent: false });
    }
    this.preferredLanguage.valueChanges
      .pipe(
        distinctUntilChanged(),
        filter(locale => !!locale),
        map(locale => locale as LOCALE),
        tap((locale) => {
          this.captureNeedService.languageChanged.set(true);
          this.accountService.setLocale(locale);
        }),
      )
      .subscribe();
  }

  getBannerContent(): void {
    this.query = {
      data: {
        logged: this.accountService.user().isAuthenticated ? "yes" : 'no'
      }
    };
    this.builderService.fetchContentQuery(BANNER_MODEL, this.query).then((data) => {
      this.builderService.bannerContent.set(data);
    });
  }


  ngOnInit(): void {
    // Set default locale
    this.translateService.get('locales.en').subscribe(() => {
      this.translateFileLoaded = true;
    });
    this.getBannerContent();
  }

  openSidenav(): void {
    this.sideNavToggle.emit(true);
  }

  openWhatsapp(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.open(this.whatsappGivaudan, '_blank');
    }
  }

  logout(): void {
    this.accountService.logout();
  }

  login(): void {
    this.accountService.initAuth();
  }

  openPopin(): void {
    this.closeCartPopin();
    this.closeCartV2Popin();
    this.menuTrigger?.openMenu();
    this.isMenuOpen = true;
  }

  closePopin(): void {
    this.menuTrigger?.closeMenu();
    this.isMenuOpen = false;
  }

  openCartPopin(): void {
    this.closePopin();
    this.menuCartTrigger?.openMenu();
    this.isMenuCartOpen = true;
    this.cartService.getCart();
  }

  closeCartPopin(): void {
    this.menuCartTrigger?.closeMenu();
    this.isMenuCartOpen = false;
  }

  openCartV2Popin(): void {
    this.closePopin();
    this.menuCartV2Trigger?.openMenu();
    this.isMenuCartV2Open = true;
    this.cartV2Service.getCart();
  }

  closeCartV2Popin(): void {
    this.menuCartV2Trigger?.closeMenu();
    this.isMenuCartV2Open = false;
  }

  protected readonly customComponents = customComponents.filter(elem => elem.tag?.includes('banner'));
  protected readonly BANNER_MODEL = BANNER_MODEL;
}
